import React, { FunctionComponent } from 'react'
import { Spinner } from 'reactstrap'

import Notes from 'components/Notes'
import { ContentSectionCard } from 'components/TailwindUIToolkit'
import * as SC from 'components/TailwindUIToolkit/StyledComponents'
import { INTERNAL_APPLICATION_SLUG } from 'constants/urls'
import { combineFirstNameLastName } from 'lib/utils'
import NoteWidgetOption from 'types/NoteWidgetOption'
import { gql, useQuery } from 'urql'

type NoteComponentPropTypes = {
  userId: string
}

type CustomerProfileNoteResourcesRentalApplication = {
  id: string
  mls_listing?: {
    display_line_1?: string | null
  } | null
}

type CustomerProfileNoteResourcesUser = {
  id: string
  first_name?: string | null
  last_name?: string | null
  rentals: Array<{
    id: string
    rental_application?: CustomerProfileNoteResourcesRentalApplication | null
  }>
  lead_group_users: Array<{
    id: string
    lead_group?: {
      id: string
      rental_applications: CustomerProfileNoteResourcesRentalApplication[]
    } | null
  }>
}

type CustomerProfileNoteResourcesQuery = {
  users: CustomerProfileNoteResourcesUser[]
}

const queryDoc = gql`
  query CustomerProfileNoteResources($id: uuid!) {
    users(where: { id: { _eq: $id } }) {
      id
      first_name
      last_name
      rentals {
        id
        rental_application {
          id
          mls_listing {
            id
            display_line_1
          }
        }
      }
      lead_group_users {
        id
        lead_group {
          id
          rental_applications {
            id
            mls_listing {
              id
              display_line_1
            }
          }
        }
      }
    }
  }
`

const formatListingName = (displayLine1?: string | null) => displayLine1 || 'unknown address'

const addUniqueOption = (
  resourceNoteWidgetOptions: NoteWidgetOption[],
  seenOptions: Set<string>,
  option: NoteWidgetOption
) => {
  const optionKey = `${option.type}:${option.id}`

  if (seenOptions.has(optionKey)) {
    return
  }

  seenOptions.add(optionKey)
  resourceNoteWidgetOptions.push(option)
}

const buildResourceNoteWidgetOptions = (
  user: CustomerProfileNoteResourcesUser
): NoteWidgetOption[] => {
  const resourceNoteWidgetOptions: NoteWidgetOption[] = []
  const seenOptions = new Set<string>()
  const userName = combineFirstNameLastName(user).trim() || 'User'

  addUniqueOption(resourceNoteWidgetOptions, seenOptions, {
    id: user.id,
    type: 'users',
    displayName: userName,
    writable: true
  })

  user.rentals.forEach((rental) => {
    const displayLine1 = rental.rental_application?.mls_listing?.display_line_1

    addUniqueOption(resourceNoteWidgetOptions, seenOptions, {
      id: rental.id,
      type: 'rentals',
      displayName: `Rental for ${formatListingName(displayLine1)}`,
      linkTo: `/rent-roll/${rental.id}`,
      writable: true
    })
  })

  user.lead_group_users.forEach((leadGroupUser) => {
    const leadGroup = leadGroupUser.lead_group

    if (!leadGroup) {
      return
    }

    const rentalApplicationNames = leadGroup.rental_applications.map((rentalApplication) =>
      formatListingName(rentalApplication.mls_listing?.display_line_1)
    )
    const rentalApplicationDisplayNames = rentalApplicationNames.length
      ? rentalApplicationNames.join(', ')
      : 'unknown address'

    addUniqueOption(resourceNoteWidgetOptions, seenOptions, {
      id: leadGroup.id,
      type: 'lead_groups',
      displayName: `Lead group for ${rentalApplicationDisplayNames}`,
      writable: true
    })

    leadGroup.rental_applications.forEach((rentalApplication) => {
      addUniqueOption(resourceNoteWidgetOptions, seenOptions, {
        id: rentalApplication.id,
        type: 'rental_applications',
        displayName: `Application for ${formatListingName(
          rentalApplication.mls_listing?.display_line_1
        )}`,
        linkTo: `${INTERNAL_APPLICATION_SLUG}${rentalApplication.id}`,
        writable: true
      })
    })
  })

  return resourceNoteWidgetOptions
}

const Note: FunctionComponent<React.PropsWithChildren<NoteComponentPropTypes>> = ({ userId }) => {
  const [{ data, fetching, error }] = useQuery<CustomerProfileNoteResourcesQuery>({
    query: queryDoc,
    variables: { id: userId },
    context: React.useMemo(() => ({ suspense: false }), [])
  })

  if (fetching) {
    return (
      <ContentSectionCard title={'Notes'} padding>
        <Spinner />
      </ContentSectionCard>
    )
  }

  if (error) {
    return (
      <ContentSectionCard title={'Notes'} padding>
        <SC.ErrorMessage>{error.message}</SC.ErrorMessage>
      </ContentSectionCard>
    )
  }

  const user = data?.users?.[0]

  if (!user) {
    return (
      <ContentSectionCard title={'Notes'} padding>
        <SC.MessageText>No notes found for user</SC.MessageText>
      </ContentSectionCard>
    )
  }

  return <Notes resourceNoteWidgetOptions={buildResourceNoteWidgetOptions(user)} />
}

export default Note
